(() => {
  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

  const track = (event, details = {}) => {
    const payload = { event, ...details };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(`yaga:${event}`, { detail: details }));
  };

  const cardMatches = (card, filter) => {
    if (filter === 'all') return true;
    const [kind, value] = filter.split(':');
    if (kind === 'type') return (card.dataset.yagaTypes || '').split(/\s+/).includes(value);
    if (kind === 'growth') return card.dataset.yagaGrowth === value;
    if (kind === 'category') return card.dataset.yagaCategory === value;
    if (kind === 'potency') return value === 'high' && Number(card.dataset.yagaPotency || 0) >= 30;
    return true;
  };

  const applyFlowerFilter = (root, filter) => {
    const scope = root.closest('.yaga-cro-flower-grid') || document;
    const cards = [...scope.querySelectorAll('[data-yaga-card]')];
    let visible = 0;

    cards.forEach((card) => {
      const matches = cardMatches(card, filter);
      card.hidden = !matches;
      card.setAttribute('aria-hidden', matches ? 'false' : 'true');
      if (matches) visible += 1;
    });

    root.dataset.activeFilter = filter;
    root.querySelectorAll('[data-yaga-filter]').forEach((button) => {
      const active = button.dataset.yagaFilter === filter;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const count = root.querySelector('[data-yaga-visible-count]');
    const label = root.dataset.yagaCountLabel || 'strain';
    const nextCount = `${visible} ${visible === 1 ? label.replace(/s$/, '') : label}`;
    if (count && count.textContent !== nextCount) count.textContent = nextCount;
    scope.querySelectorAll('#ProductCountDesktop, #ProductCount').forEach((nativeCount) => {
      if (nativeCount.textContent.trim() !== nextCount) nativeCount.textContent = nextCount;
    });
  };

  const refreshFlowerFilterCounts = (root) => {
    const scope = root.closest('.yaga-cro-flower-grid') || document;
    const cards = [...scope.querySelectorAll('[data-yaga-card]')];
    root.querySelectorAll('[data-yaga-filter]').forEach((button) => {
      const filter = button.dataset.yagaFilter;
      if (filter === 'all') return;
      const total = cards.filter((card) => cardMatches(card, filter)).length;
      const count = button.querySelector('[data-yaga-filter-count]');
      const nextCount = total ? `(${total})` : '';
      if (count && count.textContent !== nextCount) count.textContent = nextCount;
      button.hidden = total === 0;
    });
  };

  const initFlowerFilters = () => {
    document.querySelectorAll('[data-yaga-cro-filters]').forEach((root) => {
      if (!root.dataset.yagaFilterBound) {
        root.dataset.yagaFilterBound = 'true';
        root.addEventListener('click', (event) => {
          const button = event.target.closest('[data-yaga-filter]');
          if (!button) return;
          const filter = button.dataset.yagaFilter || 'all';
          applyFlowerFilter(root, filter);
          track('flower_filter_use', { filter });
        });
      }
      refreshFlowerFilterCounts(root);
      applyFlowerFilter(root, root.dataset.activeFilter || 'all');
    });
  };

  const facetKey = (label = '', details = null) => {
    const customFacet = details?.dataset.yagaCustomFacet;
    if (customFacet) return customFacet === 'highlight' ? 'highlights' : customFacet;
    const inputName = details?.querySelector('input[name]')?.name || '';
    if (inputName === 'filter.p.vendor') return 'brand';
    if (inputName.includes('filter.p.m.custom.cannabinoids')) return 'cannabinoid';
    const normalized = label.trim().replace(/\s+/g, ' ').replace(/\s*\(\d+\)\s*$/, '').toLowerCase();
    if (normalized.startsWith('shop by format') || normalized.startsWith('product category')) return 'category';
    if (normalized.startsWith('strain type') || normalized.startsWith('flower type')) return 'flower-type';
    if (normalized.startsWith('by weight') || normalized.startsWith('weight') || normalized.startsWith('size / weight')) return 'size';
    if (normalized.startsWith('cannabinoid')) return 'cannabinoid';
    if (normalized.startsWith('brand') || normalized.startsWith('vendor')) return 'brand';
    if (normalized.startsWith('price')) return 'price';
    if (normalized.startsWith('availability')) return 'availability';
    return normalized;
  };

  const facetName = (key) => ({
    category: 'Product category',
    'flower-type': 'Flower type',
    size: 'Size / weight',
    cannabinoid: 'Cannabinoid',
    brand: 'Brand',
    price: 'Price',
    availability: 'Availability',
  }[key] || '');

  const replaceFacetText = (details, key) => {
    const nextName = facetName(key);
    if (!nextName) return;
    const desktopLabel = details.querySelector(':scope > summary .facets__summary-label');
    if (desktopLabel) {
      const selected = desktopLabel.querySelector('.facets__selected');
      [...desktopLabel.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) node.textContent = `${nextName} `;
      });
      if (!selected && desktopLabel.textContent.trim() !== nextName) desktopLabel.textContent = nextName;
    }
    details.querySelectorAll(':scope > summary.mobile-facets__summary > div > span:first-child, .mobile-facets__close-button > span:not(.facets__and-helptext)').forEach((label) => {
      if (label.textContent.trim() !== nextName) label.textContent = nextName;
    });
    const summary = details.querySelector(':scope > summary');
    const selectedCount = details.querySelector('.facets__selected')?.textContent.match(/\d+/)?.[0] || '0';
    if (summary?.hasAttribute('aria-label')) summary.setAttribute('aria-label', `${nextName} (${selectedCount} selected)`);
    details.querySelectorAll('legend.visually-hidden').forEach((legend) => {
      if (legend.textContent.trim() !== nextName) legend.textContent = nextName;
    });
  };

  const cleanFacetValues = (details, key) => {
    const categoryNames = new Map([
      ['accesories', 'Accessories'],
      ['preroll', 'Prerolls'],
      ['vape', 'Vapes'],
      ['thca', 'Other THCa products'],
    ]);
    details.querySelectorAll('li').forEach((item) => {
      const label = item.querySelector('label');
      if (!label) return;
      const inputValue = item.querySelector('input')?.value?.trim() || '';
      if (key === 'category' && categoryNames.has(inputValue.toLowerCase())) {
        const nextName = categoryNames.get(inputValue.toLowerCase());
        label.querySelectorAll('.facet-checkbox__text-label, .mobile-facets__label > span:first-child').forEach((target) => {
          if (target.textContent.trim() !== nextName) target.textContent = nextName;
        });
        label.querySelectorAll('.visually-hidden').forEach((target) => {
          const count = label.textContent.match(/\((\d+)\)/)?.[1] || '0';
          const nextText = `${nextName} (${count} ${count === '1' ? 'product' : 'products'})`;
          if (target.textContent !== nextText) target.textContent = nextText;
        });
      }
    });
  };

  const cleanNativeFacets = () => {
    // Same filters, same order on every collection page. A filter that can't narrow this collection
    // (fewer than 2 selectable values and nothing selected) hides itself; Price always shows.
    const allowed = new Set(['category', 'availability', 'flower-type', 'grow', 'highlights', 'size', 'cannabinoid', 'brand', 'price']);
    const priority = ['category', 'availability', 'flower-type', 'grow', 'highlights', 'size', 'cannabinoid', 'brand', 'price'];

    document.querySelectorAll('.facets__heading--vertical').forEach((heading) => {
      if (heading.textContent.trim() !== 'Refine results') heading.textContent = 'Refine results';
    });

    document.querySelectorAll('#main-collection-filters details').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      const label = summary?.textContent?.trim().replace(/\s+/g, ' ').toLowerCase() || '';
      const isUtilityDrawer = label.startsWith('filter and sort');
      if (isUtilityDrawer) return;
      const key = facetKey(label, details);
      const isPrice = key === 'price';
      const isCustom = Boolean(details.dataset.yagaCustomFacet);
      const choices = [...details.querySelectorAll('input[type="checkbox"], input[type="radio"]')];
      const selectable = choices.filter((input) => !input.disabled).length;
      const selected = choices.some((input) => input.checked);
      const hidden = !allowed.has(key) || (!isPrice && !isCustom && selectable < 2 && !selected);
      details.hidden = hidden;
      details.dataset.yagaFacetHidden = hidden ? 'true' : 'false';
      details.dataset.yagaFacet = key;
      replaceFacetText(details, key);
      cleanFacetValues(details, key);
    });

    ['#FacetsWrapperDesktop', '#FacetsWrapperMobile'].forEach((selector) => {
      const wrapper = document.querySelector(selector);
      if (!wrapper) return;
      const details = [...wrapper.querySelectorAll(':scope > details[data-yaga-facet]')];
      const sorted = [...details].sort((a, b) => {
        const aIndex = priority.indexOf(a.dataset.yagaFacet);
        const bIndex = priority.indexOf(b.dataset.yagaFacet);
        return (aIndex < 0 ? 99 : aIndex) - (bIndex < 0 ? 99 : bIndex);
      });
      if (details.some((item, index) => item !== sorted[index])) {
        const mobileFooter = wrapper.querySelector(':scope > .mobile-facets__footer');
        sorted.forEach((item) => wrapper.insertBefore(item, mobileFooter));
      }
    });
  };

  const simplifyMemberPricing = () => {
    document.querySelectorAll('.yaga-cro-collection-grid .member-price-label, .yaga-home-concept .member-price-label').forEach((label) => {
      const amounts = label.textContent.match(/\$[\d,]+(?:\.\d{2})?/g) || [];
      if (!amounts.length) return;
      const nextText = amounts.length > 1 ? `Insider from ${amounts[0]}` : `Insider ${amounts[0]}`;
      if (label.textContent !== nextText) label.textContent = nextText;
    });
  };

  const syncMobileMemberPrice = () => {
    const summary = document.querySelector('[data-yaga-mobile-summary]');
    if (!summary) return;
    const source = [...document.querySelectorAll('.product--yaga-cro .member-price-label')]
      .find((label) => !label.closest('[data-yaga-mobile-summary]'));
    if (!source) return;
    const amounts = source.textContent.match(/\$[\d,]+(?:\.\d{2})?/g) || [];
    if (!amounts.length) return;
    let target = summary.querySelector('[data-yaga-mobile-member]');
    if (!target) {
      target = document.createElement('p');
      target.className = 'yaga-cro-mobile-summary__member';
      target.dataset.yagaMobileMember = '';
      summary.querySelector('[data-yaga-mobile-price]')?.after(target);
    }
    const nextText = `Insider ${amounts[0]} · after first purchase`;
    if (target.textContent !== nextText) target.textContent = nextText;
  };

  const improveAgeGate = () => {
    const agree = document.querySelector('.preview_box .agree_btn span');
    const disagree = document.querySelector('.preview_box .disagree_btn span');
    if (agree && agree.textContent.trim() === 'Agree') agree.textContent = 'Enter site — I’m 21+';
    if (disagree && disagree.textContent.trim() === 'Disagree') disagree.textContent = 'Exit';
  };

  const getProductVariants = () => {
    const script = document.querySelector('[data-yaga-cro-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  };

  const getPickerVariant = (variants) => {
    const picker = document.querySelector('.product--yaga-cro variant-selects');
    if (!picker) return null;
    const selectedOptions = [...picker.querySelectorAll('fieldset, .product-form__input')]
      .map((group) => group.querySelector('input[type="radio"]:checked')?.value || group.querySelector('select')?.value)
      .filter(Boolean);
    if (!selectedOptions.length) return null;
    return variants.find(
      (variant) => variant.options?.length === selectedOptions.length
        && variant.options.every((option, index) => option === selectedOptions[index])
    ) || null;
  };

  const getSelectedVariant = (variants) => {
    const idInput = document.querySelector('.product--yaga-cro .product-form .product-variant-id');
    const selectedId = String(idInput?.value || '');
    return getPickerVariant(variants)
      || variants.find((variant) => String(variant.id) === selectedId)
      || variants.find((variant) => variant.available)
      || variants[0];
  };

  const ensureVariantInput = (variant) => {
    if (!variant) return;
    const inputs = document.querySelectorAll('.product--yaga-cro .product-form .product-variant-id');
    inputs.forEach((input) => {
      const nextId = String(variant.id);
      if (input.value === nextId && input.getAttribute('value') === nextId) return;
      input.value = nextId;
      input.setAttribute('value', nextId);
      input.dataset.yagaVariantReady = nextId;
    });
  };

  const formatMoney = (value) => moneyFormatter.format(Number(value || 0) / 100);

  const updateMobileProductPrice = (variant) => {
    if (!variant) return;
    const priceBox = document.querySelector('[data-yaga-mobile-price]');
    if (!priceBox) return;

    let compare = priceBox.querySelector('s');
    const price = priceBox.querySelector('strong');
    const title = priceBox.querySelector('span');
    const hasSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);

    if (hasSale && !compare) {
      compare = document.createElement('s');
      priceBox.prepend(compare);
    }
    if (compare) {
      compare.hidden = !hasSale;
      const nextCompare = hasSale ? formatMoney(variant.compare_at_price) : '';
      if (compare.textContent !== nextCompare) compare.textContent = nextCompare;
    }
    const nextPrice = formatMoney(variant.price);
    if (price && price.textContent !== nextPrice) price.textContent = nextPrice;
    if (title && title.textContent !== variant.title) title.textContent = variant.title;

    const stickyPrice = document.querySelector('[data-yaga-sticky-price]');
    const stickyVariant = document.querySelector('[data-yaga-sticky-variant]');
    if (stickyPrice && stickyPrice.textContent !== nextPrice) stickyPrice.textContent = nextPrice;
    if (stickyVariant && stickyVariant.textContent !== variant.title) stickyVariant.textContent = variant.title;
  };

  const valueUnitForLabel = (label) => {
    const normalized = label.trim().toLowerCase();
    if (normalized.includes('quarter pound')) return { divisor: 113.4, suffix: 'g' };
    if (normalized.includes('half pound')) return { divisor: 226.8, suffix: 'g' };
    if (normalized === 'pound' || normalized === '1 pound') return { divisor: 453.6, suffix: 'g' };
    if (normalized.includes('ounce') || normalized === '28g') return { divisor: 28, suffix: 'g' };
    const grams = normalized.match(/([\d.]+)\s*g(?:\b|$)/);
    if (grams) return { divisor: Number(grams[1]), suffix: 'g' };
    const pack = normalized.match(/(?:pack|case)\s+of\s+(\d+)|(\d+)\s*[- ]?pack/);
    const units = Number(pack?.[1] || pack?.[2] || 0);
    return units > 1 ? { divisor: units, suffix: 'ea' } : null;
  };

  const addVariantValueCues = (variants) => {
    if (!variants.length || variants.some((variant) => (variant.options || []).length !== 1)) return;
    document.querySelectorAll('.product--yaga-cro .product-form__input input[type="radio"]').forEach((input) => {
      const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label || label.querySelector('.yaga-cro-variant-value')) return;
      const unit = valueUnitForLabel(input.value);
      const variant = variants.find((item) => item.options?.includes(input.value));
      if (!unit || !variant) return;
      const value = document.createElement('span');
      value.className = 'yaga-cro-variant-value';
      value.textContent = `${moneyFormatter.format(Number(variant.price || 0) / 100 / unit.divisor)}/${unit.suffix}`;
      label.append(value);
    });
  };

  const initProductCRO = () => {
    const product = document.querySelector('.product--yaga-cro');
    if (!product) return;
    const variants = getProductVariants();
    if (!variants.length) return;

    const selectedVariant = getSelectedVariant(variants);
    addVariantValueCues(variants);
    updateMobileProductPrice(selectedVariant);
    [0, 180, 700, 1800].forEach((delay) => {
      window.setTimeout(() => ensureVariantInput(getSelectedVariant(variants)), delay);
    });

    if (!product.dataset.yagaVariantBound) {
      product.dataset.yagaVariantBound = 'true';
      product.addEventListener('change', () => {
        [0, 120, 350].forEach((delay) => {
          window.setTimeout(() => {
            const variant = getSelectedVariant(variants);
            ensureVariantInput(variant);
            updateMobileProductPrice(variant);
          }, delay);
        });
      });
      product.addEventListener('submit', (event) => {
        if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
        ensureVariantInput(getSelectedVariant(variants));
      }, true);
    }

    const sticky = document.querySelector('[data-yaga-sticky-cart]');
    if (!sticky || sticky.dataset.yagaStickyBound) return;
    sticky.dataset.yagaStickyBound = 'true';
    const stickyButton = sticky.querySelector('[data-yaga-sticky-submit]');

    const mainButton = () => document.querySelector('.product--yaga-cro .product-form__submit[name="add"]');
    const updateSticky = () => {
      const button = mainButton();
      if (!button || window.innerWidth > 749) {
        sticky.classList.remove('is-visible');
        sticky.setAttribute('aria-hidden', 'true');
        return;
      }
      const rect = button.getBoundingClientRect();
      const buttonVisible = rect.top < window.innerHeight && rect.bottom > 0;
      const show = window.scrollY > 180 && !buttonVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      stickyButton.disabled = button.disabled;
    };

    stickyButton?.addEventListener('click', () => {
      const button = mainButton();
      if (!button || button.disabled) return;
      track('product_sticky_add_to_cart', {
        productKind: product.dataset.yagaProductKind || 'general',
        variant: getSelectedVariant(variants)?.title || '',
      });
      button.click();
    });
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);
    updateSticky();
  };

  const bindCardTracking = () => {
    if (document.documentElement.dataset.yagaCardTrackingBound) return;
    document.documentElement.dataset.yagaCardTrackingBound = 'true';
    document.addEventListener('click', (event) => {
      const menuToggle = event.target.closest('.yaga-header-menu > summary, #Details-menu-drawer-container > summary');
      if (menuToggle) {
        track('navigation_menu_toggle', { menu: menuToggle.textContent.trim() || 'mobile' });
      }
      const navigationLink = event.target.closest('[data-yaga-nav-link], .menu-drawer__menu a, .header__inline-menu a');
      if (navigationLink) {
        track('navigation_link_click', {
          location: navigationLink.dataset.yagaNavLink || (navigationLink.closest('.menu-drawer') ? 'mobile-menu' : 'desktop-menu'),
          label: navigationLink.textContent.trim(),
          path: navigationLink.pathname || '',
        });
        return;
      }
      const newsletterButton = event.target.closest('#Subscribe');
      if (newsletterButton) {
        track('footer_newsletter_submit');
        return;
      }
      const cardButton = event.target.closest('.yaga-cro-card .quick-add__submit');
      if (cardButton) {
        const root = cardButton.closest('[data-yaga-collection-kind]');
        const title = cardButton.closest('.card-wrapper')?.querySelector('.card__heading')?.textContent?.trim() || '';
        track('product_card_action', {
          collectionKind: root?.dataset.yagaCollectionKind || 'not-a-collection',
          pageType: document.body.className.match(/template-[\w-]+/)?.[0] || 'storefront',
          product: title,
        });
        return;
      }
      const checkoutButton = event.target.closest('#checkout');
      if (checkoutButton) {
        track('cart_checkout_start');
        return;
      }
      const homeButton = event.target.closest('.yaga-home-product [data-yaga-add]');
      if (homeButton) {
        const title = homeButton.closest('.yaga-home-product')?.querySelector('h3')?.textContent?.trim() || '';
        track('home_featured_product_action', { product: title });
      }
    });
  };

  const refresh = () => {
    initFlowerFilters();
    cleanNativeFacets();
    simplifyMemberPricing();
    syncMobileMemberPrice();
    improveAgeGate();
    initProductCRO();
    bindCardTracking();
  };

  let queued = false;
  const queueRefresh = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      refresh();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  new MutationObserver(queueRefresh).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', refresh);

  window.YagaFlowerCRO = {
    refresh,
    filter(filter = 'all') {
      const root = document.querySelector('[data-yaga-cro-filters]');
      if (!root) return 0;
      applyFlowerFilter(root, filter);
      return document.querySelectorAll('.yaga-cro-flower-grid [data-yaga-card]:not([hidden])').length;
    },
  };
  document.documentElement.dataset.yagaCroReady = 'true';
})();
